import React, { useEffect, useMemo, useState } from "react";

import "../../css/StaffHoliday/StaffHoliday.css";
import "../../css/StaffHoliday/StaffHolidaySplitView.css";
import "../../css/StaffHoliday/StaffHolidayApprovalPage.css";
import "../../css/Marketing/FinancialPromotions/FinancialPromotions.css";

import Sidebar from "../DesignSystem/Sidebar";
import Banner from "../DesignSystem/Banner/Banner";
import Breadcrumb from "../DesignSystem/Breadcrumb/Breadcrumb";
import CustomSelect from "../DesignSystem/Dropdowns/CustomSelect";
import DateField from "../DesignSystem/Inputs/DateField";
import RadioGroup from "../DesignSystem/SelectionControls/RadioGroup";
import TextArea from "../DesignSystem/Inputs/TextArea";
import Tabs from "../DesignSystem/Tabs/Tabs";
import StatusButton from "../DesignSystem/Buttons/StatusButton";

import StaffHolidayActionConfirmModal from "./components/StaffHolidayActionConfirmModal";
import StaffHolidayEmailPreviewModal from "./StaffHolidayEmailPreviewModal";
import { ReactComponent as ArrowUp } from "../../assets/icons/financialPromotions/expand-arrow.svg";
import { ReactComponent as ArrowDown } from "../../assets/icons/arrow-down.svg";

import { useStaffHolidayRequests } from "./hooks/useStaffHolidayRequests";
import StaffHolidayExcelDownload from "./components/StaffHolidayExcelDownload";
import StaffHolidayTrackerPanel from "./StaffHolidayTrackerPanel";
import {
  buildLeaveStatCards,
  buildOverallLeaveStats,
  buildStaffHolidayProfiles,
  formatNumber,
  getRequestStaffEmail,
  getLatestLeaveHistoryEntries,
  getRequestStaffName,
  getStaffDirectoryDisplayName,
  getStatusTone,
  hasStaffDepartment,
  isApprovedStatus,
  isCancelledStatus,
  isInactiveStaff,
  isPendingApprovalStatus,
  isSummaryStaffDirectoryItem,
  isStaffEmployedInHolidayYear,
  normaliseEmail,
  normalizeStaffDirectoryItem,
} from "./utils/staffHolidayStaffUtils";
import { Helmet } from "react-helmet-async";

import {
  DAY_TYPES,
  LEAVE_STATUS,
  LEAVE_STATUS_LABELS,
  LEAVE_TYPES,
  LEAVE_TYPE_LABELS,
  ADMIN_LEAVE_TYPE_LABELS,
} from "./utils/staffHolidayConstants";

import {
  formatDisplayDate,
  toDateKey,
} from "./utils/staffHolidayDateUtils";
import { fetchStaffHolidayDirectoryWithBalances } from "./utils/staffHolidayExcelImport";

import { auth } from "../../backend/firebaseConfig";

import {
  rejectStaffHolidayRequest,
  submitStaffHolidayRequest,
  updateStaffHolidayRequest,
  deleteStaffHolidayRequest,
} from "./services/staffHolidayFirebase";
import PrimaryButton from "../DesignSystem/Buttons/PrimaryButton";

import {
  hasFinanceAdminPermission,
  hasHolidayAdminPermission,
} from "./utils/staffHolidayPermissions";
import { useUserPermissions } from "../SignIn/UserPermissionsContext";

const CURRENT_HOLIDAY_YEAR = new Date().getFullYear();

const sortActiveStaffFirst = (a, b) => {
  const inactiveDifference =
    Number(isInactiveStaff(a)) - Number(isInactiveStaff(b));

  if (inactiveDifference !== 0) return inactiveDifference;

  return String(a.displayName || a.name).localeCompare(
    String(b.displayName || b.name),
  );
};

const requestOverlapsYear = (request = {}, year) => {
  const startDate = toDateKey(request.startDate || request.date);
  const endDate = toDateKey(
    request.endDate || request.startDate || request.date,
  );
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;

  return Boolean(
    startDate && endDate && startDate <= yearEnd && endDate >= yearStart,
  );
};

const VIEW_MODES = {
  ADMIN: "admin",
};

const TAB_IDS = {
  PENDING_REQUEST: "pending-request",
  ADD_HOLIDAY: "add-holiday",
  SUMMARY: "summary",
};

const EMPTY_FORM = {
  editRequestId: "",
  originalStaffEmail: "",
  staffEmail: "",
  leaveType: LEAVE_TYPES.ANNUAL_LEAVE,
  startDate: "",
  endDate: "",
  startDayType: DAY_TYPES.FULL,
  endDayType: DAY_TYPES.FULL,
  status: LEAVE_STATUS.APPROVED,
  staffNotes: "",
  updateReason: "",
};

const getRequestId = (request = {}) => request.requestId || request.id;

const formatAuditDateTime = (value) => {
  if (!value) return "-";

  const date = value?.toDate ? value.toDate() : new Date(value);

  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getAuditTrail = (item = {}) => {
  return Array.isArray(item.auditTrail) ? item.auditTrail : [];
};

const toTitleCase = (value = "") => {
  return String(value || "")
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const formatAuditAction = (action = "") => {
  return toTitleCase(action);
};

const formatAuditLeaveType = (leaveType = "") => {
  return ADMIN_LEAVE_TYPE_LABELS[leaveType] || toTitleCase(leaveType) || "-";
};

const formatAuditStatus = (status = "") => {
  return LEAVE_STATUS_LABELS[status] || toTitleCase(status) || "-";
};

const formatAuditDayType = (dayType = "") => {
  return toTitleCase(dayType) || "-";
};

const formatAuditValue = (label, value) => {
  if (value === undefined || value === null || value === "") return null;

  return {
    label,
    value,
  };
};

const getAuditSnapshotRows = (snapshot = {}) => {
  if (!snapshot || typeof snapshot !== "object") return [];

  return [
    formatAuditValue("Type", formatAuditLeaveType(snapshot.leaveType)),
    formatAuditValue("Start date", formatDisplayDate(snapshot.startDate)),
    formatAuditValue("End date", formatDisplayDate(snapshot.endDate)),
    formatAuditValue("Date", formatDisplayDate(snapshot.date)),
    formatAuditValue("Start day", formatAuditDayType(snapshot.startDayType)),
    formatAuditValue("End day", formatAuditDayType(snapshot.endDayType)),
    formatAuditValue("Days", snapshot.totalDays),
    formatAuditValue("Day value", snapshot.dayValue),
    formatAuditValue("Status", formatAuditStatus(snapshot.status)),
    formatAuditValue("Notes", snapshot.staffNotes),
    formatAuditValue("Reason", snapshot.reason),
  ].filter(Boolean);
};

const AUDIT_FIELD_CONFIG = [
  {
    key: "leaveType",
    label: "Type",
    format: (value) => formatAuditLeaveType(value),
  },
  {
    key: "startDate",
    label: "Start date",
    format: (value) => formatDisplayDate(value),
  },
  {
    key: "endDate",
    label: "End date",
    format: (value) => formatDisplayDate(value),
  },
  {
    key: "date",
    label: "Date",
    format: (value) => formatDisplayDate(value),
  },
  {
    key: "startDayType",
    label: "Start day",
    format: (value) => formatAuditDayType(value),
  },
  {
    key: "endDayType",
    label: "End day",
    format: (value) => formatAuditDayType(value),
  },
  {
    key: "totalDays",
    label: "Days",
    format: (value) => value,
  },
  {
    key: "dayValue",
    label: "Day value",
    format: (value) => value,
  },
  {
    key: "status",
    label: "Status",
    format: (value) => formatAuditStatus(value),
  },
  {
    key: "staffNotes",
    label: "Notes",
    format: (value) => value,
  },
  {
    key: "reason",
    label: "Reason",
    format: (value) => value,
  },
];

const getComparableAuditValue = (snapshot = {}, key = "") => {
  const value = snapshot?.[key];

  if (value === undefined || value === null) return "";

  return String(value).trim();
};

const getAuditChangedRows = (before = {}, after = {}) => {
  return AUDIT_FIELD_CONFIG.map((field) => {
    const beforeRaw = getComparableAuditValue(before, field.key);
    const afterRaw = getComparableAuditValue(after, field.key);

    if (!beforeRaw && !afterRaw) return null;

    if (beforeRaw === afterRaw) return null;

    return {
      key: field.key,
      label: field.label,
      before: beforeRaw ? field.format(beforeRaw) : "-",
      after: afterRaw ? field.format(afterRaw) : "-",
    };
  }).filter(Boolean);
};

const renderAuditChanges = (before = {}, after = {}) => {
  const rows = getAuditChangedRows(before, after);

  if (rows.length === 0) {
    return (
      <p className="FP_Help_Text grey staff-holiday-audit-no-change">
        No field changes recorded.
      </p>
    );
  }

  return (
    <div className="staff-holiday-audit-change-list">
      {rows.map((row) => (
        <div key={row.key} className="staff-holiday-audit-change-row">
          <span>{row.label}</span>

          <div>
            <p>
              <strong>Before:</strong> {row.before}
            </p>
            <p>
              <strong>After:</strong> {row.after}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

const getHolidayManagers = (item = {}) => {
  return Array.isArray(item.holidayManagers) ? item.holidayManagers : [];
};

const matchesHolidayManagerView = (item = {}, managerEmail = "") => {
  const normalizedManagerEmail = normaliseEmail(managerEmail);

  if (!normalizedManagerEmail || normalizedManagerEmail === "all") {
    return true;
  }

  return getHolidayManagers(item).some(
    (manager) =>
      normaliseEmail(manager.email || manager.id || manager) ===
      normalizedManagerEmail,
  );
};

const StaffHolidayApprovalsPage = () => {
  const mode = VIEW_MODES.ADMIN;

  const currentUserEmail = String(
    auth.currentUser?.email || localStorage.getItem("userEmail") || "",
  )
    .trim()
    .toLowerCase();

  const { userPermissions } = useUserPermissions();

  const isSuperAdmin = useMemo(() => {
    const permissions = Array.isArray(userPermissions) ? userPermissions : [];
    return permissions.some(
      (p) =>
        String(p || "")
          .trim()
          .toLowerCase() === "super holiday admin" ||
        String(p || "")
          .trim()
          .toLowerCase() === "administrator",
    );
  }, [userPermissions]);

  const canDownloadExcel = useMemo(() => {
    return isSuperAdmin;
  }, [isSuperAdmin, userPermissions]);

  const [preview, setPreview] = useState(null);
  const [busyId, setBusyId] = useState("");
  const [message, setMessage] = useState("");
  const [approvalSuccessBanner, setApprovalSuccessBanner] = useState("");
  const [selectedHolidayManagerView, setSelectedHolidayManagerView] =
    useState("all");
  const [activeTab, setActiveTab] = useState(TAB_IDS.PENDING_REQUEST);
  const [selectedPendingRequestId, setSelectedPendingRequestId] = useState("");
  const [summaryYear, setSummaryYear] = useState(CURRENT_HOLIDAY_YEAR);

  const [staffDirectory, setStaffDirectory] = useState([]);
  const [staffDirectoryLoading, setStaffDirectoryLoading] = useState(false);
  const [summaryStaffDirectory, setSummaryStaffDirectory] = useState([]);
  const [summaryStaffDirectoryLoading, setSummaryStaffDirectoryLoading] =
    useState(false);

  const [createBusy, setCreateBusy] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);

  const [actionModal, setActionModal] = useState({
    open: false,
    action: "",
    request: null,
    reason: "",
  });

  useEffect(() => {
    if (!approvalSuccessBanner) return undefined;

    const timeoutId = window.setTimeout(() => {
      setApprovalSuccessBanner("");
    }, 3000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [approvalSuccessBanner]);

  useEffect(() => {
    let cancelled = false;

    const loadStaffDirectory = async () => {
      setStaffDirectoryLoading(true);

      try {
        const selectedYear = CURRENT_HOLIDAY_YEAR;

        const staff = await fetchStaffHolidayDirectoryWithBalances({
          year: selectedYear,
        });

        const normalized = (staff || [])
          .map((item) => normalizeStaffDirectoryItem(item, selectedYear))
          .filter((item) => Boolean(item.email))
          .filter(hasStaffDepartment)
          .filter((item) => isStaffEmployedInHolidayYear(item, selectedYear))
          .sort((a, b) =>
            String(a.displayName).localeCompare(String(b.displayName)),
          );
        setStaffDirectory(normalized);
      } catch (error) {
        // console.error("Failed to load staff directory:", error);
      } finally {
        if (!cancelled) {
          setStaffDirectoryLoading(false);
        }
      }
    };

    loadStaffDirectory();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadSummaryStaffDirectory = async () => {
      setSummaryStaffDirectoryLoading(true);

      try {
        const staff = await fetchStaffHolidayDirectoryWithBalances({
          year: summaryYear,
        });

        const normalized = (staff || [])
          .map((item) => normalizeStaffDirectoryItem(item, summaryYear))
          .filter(isSummaryStaffDirectoryItem)
          .sort(sortActiveStaffFirst);

        if (!cancelled) {
          setSummaryStaffDirectory(normalized);
        }
      } catch (error) {
        if (!cancelled) {
          setSummaryStaffDirectory([]);
        }
      } finally {
        if (!cancelled) {
          setSummaryStaffDirectoryLoading(false);
        }
      }
    };

    loadSummaryStaffDirectory();

    return () => {
      cancelled = true;
    };
  }, [summaryYear]);

  const requestSubscriptionMode =
    isSuperAdmin && selectedHolidayManagerView !== "all" ? "manager" : mode;

  const requestSubscriptionEmail =
    isSuperAdmin && selectedHolidayManagerView !== "all"
      ? selectedHolidayManagerView
      : currentUserEmail;

  const {
    requests,
    loading: requestsLoading,
    error: requestsError,
  } = useStaffHolidayRequests({
    currentUserEmail: requestSubscriptionEmail,
    mode: requestSubscriptionMode,
  });

  const holidayManagerViewOptions = useMemo(() => {
    const managerMap = new Map();

    staffDirectory.forEach((staff) => {
      getHolidayManagers(staff).forEach((manager) => {
        const email = normaliseEmail(manager.email || manager.id || manager);

        if (!email || managerMap.has(email)) return;

        const label = String(
          manager.name || manager.displayName || email,
        ).trim();
        managerMap.set(email, {
          value: email,
          label,
        });
      });
    });

    return [
      { value: "all", label: "All holiday managers" },
      ...Array.from(managerMap.values()).sort((a, b) =>
        a.label.localeCompare(b.label),
      ),
    ];
  }, [staffDirectory]);

  const effectiveHolidayManagerView = isSuperAdmin
    ? selectedHolidayManagerView
    : currentUserEmail;

  const managedStaffDirectory = useMemo(() => {
    return staffDirectory.filter((staff) =>
      matchesHolidayManagerView(staff, effectiveHolidayManagerView),
    );
  }, [staffDirectory, effectiveHolidayManagerView]);

  const managedStaffEmailSet = useMemo(() => {
    return new Set(
      managedStaffDirectory.map((staff) =>
        normaliseEmail(staff.email || staff.id),
      ),
    );
  }, [managedStaffDirectory]);

  const visibleRequests = useMemo(() => {
    if (requestSubscriptionMode === "manager") {
      return requests;
    }

    if (!effectiveHolidayManagerView || effectiveHolidayManagerView === "all") {
      return requests;
    }

    return requests.filter((request) =>
      managedStaffEmailSet.has(normaliseEmail(request.staffEmail)),
    );
  }, [
    requests,
    effectiveHolidayManagerView,
    managedStaffEmailSet,
    requestSubscriptionMode,
  ]);

  const pendingRequests = useMemo(() => {
    return visibleRequests.filter(
      (request) =>
        isPendingApprovalStatus(request.status) &&
        requestOverlapsYear(request, summaryYear),
    );
  }, [summaryYear, visibleRequests]);

  useEffect(() => {
    if (pendingRequests.length === 0) {
      setSelectedPendingRequestId("");
      return;
    }

    const selectedRequestStillExists = pendingRequests.some(
      (request) => getRequestId(request) === selectedPendingRequestId,
    );

    if (!selectedRequestStillExists) {
      setSelectedPendingRequestId(getRequestId(pendingRequests[0]));
    }
  }, [pendingRequests, selectedPendingRequestId]);

  const selectedPendingRequest = useMemo(() => {
    return (
      pendingRequests.find(
        (request) => getRequestId(request) === selectedPendingRequestId,
      ) ||
      pendingRequests[0] ||
      null
    );
  }, [pendingRequests, selectedPendingRequestId]);

  const selectedRequestStaff = useMemo(() => {
    if (!selectedPendingRequest) return null;

    const selectedStaffEmail = normaliseEmail(
      getRequestStaffEmail(selectedPendingRequest),
    );

    return (
      staffDirectory.find(
        (staff) =>
          normaliseEmail(staff.email || staff.id) === selectedStaffEmail,
      ) || null
    );
  }, [selectedPendingRequest, staffDirectory]);

  const selectedRequestDepartment = useMemo(() => {
    const requestDepartment = selectedPendingRequest?.department;
    const staffDepartment = selectedRequestStaff?.department;
    const firstStaffDepartment = Array.isArray(
      selectedRequestStaff?.departments,
    )
      ? selectedRequestStaff.departments[0]
      : null;

    const id = String(
      selectedPendingRequest?.departmentId ||
        requestDepartment?.id ||
        selectedRequestStaff?.departmentId ||
        staffDepartment?.id ||
        firstStaffDepartment?.id ||
        "",
    ).trim();

    const name = String(
      selectedPendingRequest?.departmentName ||
        requestDepartment?.name ||
        selectedRequestStaff?.departmentName ||
        staffDepartment?.name ||
        firstStaffDepartment?.name ||
        (typeof staffDepartment === "string" ? staffDepartment : "") ||
        "No Department",
    ).trim();

    return {
      id,
      name: name || "No Department",
    };
  }, [selectedPendingRequest, selectedRequestStaff]);

  const totalActionableCount = pendingRequests.length;

  const staffProfiles = useMemo(() => {
    return buildStaffHolidayProfiles({
      staffDirectory,
      requests,
      selectedYear: CURRENT_HOLIDAY_YEAR,
    });
  }, [requests, staffDirectory]);

  const filteredStaffProfiles = useMemo(() => {
    return staffProfiles.filter((staff) =>
      managedStaffEmailSet.has(staff.email),
    );
  }, [staffProfiles, managedStaffEmailSet]);

  const summaryRequests = useMemo(
    () =>
      visibleRequests.filter((request) =>
        requestOverlapsYear(request, summaryYear),
      ),
    [summaryYear, visibleRequests],
  );

  const summaryManagedStaffDirectory = useMemo(
    () =>
      summaryStaffDirectory.filter((staff) =>
        matchesHolidayManagerView(staff, effectiveHolidayManagerView),
      ),
    [summaryStaffDirectory, effectiveHolidayManagerView],
  );

  const summaryStaffProfiles = useMemo(
    () =>
      buildStaffHolidayProfiles({
        staffDirectory: summaryManagedStaffDirectory,
        requests: summaryRequests,
        selectedYear: summaryYear,
      }).sort(sortActiveStaffFirst),
    [summaryManagedStaffDirectory, summaryRequests, summaryYear],
  );

  const summaryStaffProfileByEmail = useMemo(
    () =>
      new Map(
        summaryStaffProfiles.map((staff) => [
          normaliseEmail(staff.email),
          staff,
        ]),
      ),
    [summaryStaffProfiles],
  );

  const overallLeaveStats = useMemo(() => {
    return buildOverallLeaveStats(summaryStaffProfiles);
  }, [summaryStaffProfiles]);

  const overallStatCards = useMemo(() => {
    const cards = buildLeaveStatCards(overallLeaveStats);

    return cards.map((card) => {
      if (card.id !== "pending") return card;

      return {
        ...card,
        value: formatNumber(totalActionableCount),
      };
    });
  }, [overallLeaveStats, totalActionableCount]);

  const tabs = useMemo(
    () => [
      {
        id: TAB_IDS.PENDING_REQUEST,
        label: `Pending Request (${summaryYear})`,
        count: totalActionableCount,
      },
      {
        id: TAB_IDS.ADD_HOLIDAY,
        label: createForm.editRequestId ? "Edit Holiday" : "Add Holiday",
      },
      {
        id: TAB_IDS.SUMMARY,
        label: "Summary",
        count: filteredStaffProfiles.length,
      },
    ],
    [
      totalActionableCount,
      filteredStaffProfiles.length,
      createForm.editRequestId,
      summaryYear,
    ],
  );

  const staffSelectOptions = useMemo(() => {
    return managedStaffDirectory.map((staff) => ({
      id: normaliseEmail(staff.email || staff.id),
      name: `${getStaffDirectoryDisplayName(staff)} - ${normaliseEmail(
        staff.email || staff.id,
      )}`,
    }));
  }, [managedStaffDirectory]);

  const leaveTypeOptions = useMemo(() => {
    return Object.entries(ADMIN_LEAVE_TYPE_LABELS).map(([id, name]) => ({
      id,
      name,
    }));
  }, []);

  const statusOptions = useMemo(
    () => [
      {
        id: LEAVE_STATUS.PENDING_HOLIDAY_MANAGER,
        name: "Pending Holiday Manager",
      },
      {
        id: LEAVE_STATUS.PENDING_ADMIN,
        name: "Pending Admin",
      },
      {
        id: LEAVE_STATUS.APPROVED,
        name: "Approved",
      },
      {
        id: LEAVE_STATUS.REJECTED,
        name: "Rejected",
      },
      {
        id: LEAVE_STATUS.CANCELLED,
        name: "Cancelled",
      },
    ],
    [],
  );

  const dayTypeOptions = useMemo(
    () => [
      { id: DAY_TYPES.FULL, name: "Full Day" },
      { id: DAY_TYPES.MORNING, name: "Morning" },
      { id: DAY_TYPES.AFTERNOON, name: "Afternoon" },
    ],
    [],
  );

  const handleCreateFieldChange = (field, value) => {
    setCreateForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetCreateForm = () => {
    setCreateForm(EMPTY_FORM);
  };

  const handleEditRequest = (request) => {
    setMessage("");
    setPreview(null);

    setCreateForm({
      editRequestId: getRequestId(request),
      originalStaffEmail: request.staffEmail,
      staffEmail: request.staffEmail,
      leaveType: request.leaveType || LEAVE_TYPES.ANNUAL_LEAVE,
      startDate: request.startDate || "",
      endDate: request.endDate || "",
      startDayType: request.startDayType || DAY_TYPES.FULL,
      endDayType: request.endDayType || DAY_TYPES.FULL,
      status: request.status || LEAVE_STATUS.PENDING_ADMIN,
      staffNotes: request.staffNotes || "",
      updateReason: "",
    });

    setActiveTab(TAB_IDS.ADD_HOLIDAY);
  };

  const handleCreateRequest = async () => {
    setMessage("");

    if (!createForm.staffEmail) {
      setMessage("Please select a staff member.");
      return;
    }

    if (!createForm.startDate || !createForm.endDate) {
      setMessage("Please select start and end dates.");
      return;
    }

    if (createForm.endDate < createForm.startDate) {
      setMessage("End date cannot be before start date.");
      return;
    }

    setCreateBusy(true);

    try {
      if (createForm.editRequestId) {
        await updateStaffHolidayRequest({
          staffEmail: createForm.originalStaffEmail || createForm.staffEmail,
          requestId: createForm.editRequestId,

          leaveType: createForm.leaveType,
          startDate: createForm.startDate,
          endDate: createForm.endDate,
          startDayType: createForm.startDayType,
          endDayType: createForm.endDayType,
          staffNotes: createForm.staffNotes,

          status: createForm.status,
          updatedBy: currentUserEmail,
          updateReason: createForm.updateReason,

          adminEdit: true,
          notifyStaff: true,
        });

        setMessage("Holiday request updated and staff member notified.");
      } else {
        await submitStaffHolidayRequest({
          staffEmail: createForm.staffEmail,
          leaveType: createForm.leaveType,
          startDate: createForm.startDate,
          endDate: createForm.endDate,
          startDayType: createForm.startDayType,
          endDayType: createForm.endDayType,
          staffNotes: createForm.staffNotes,

          createdBy: currentUserEmail,
          autoApprove: true,
          approvedBy: currentUserEmail,
          notifyStaff: true,
        });

        setMessage("Staff holiday added, approved and staff member notified.");
      }

      resetCreateForm();
    } catch (error) {
      // console.error(error);
      setMessage(error.message || "Failed to save staff request.");
    } finally {
      setCreateBusy(false);
    }
  };

  const openActionModal = (request, action) => {
    setMessage("");

    setActionModal({
      open: true,
      action,
      request,
      reason: "",
    });
  };

  const closeActionModal = () => {
    if (busyId) return;

    setActionModal({
      open: false,
      action: "",
      request: null,
      reason: "",
    });
  };

  const updateActionReason = (reason) => {
    setActionModal((prev) => ({
      ...prev,
      reason,
    }));
  };

  const confirmActionModal = async () => {
    const request = actionModal.request;
    const requestId = getRequestId(request);

    if (!request || !requestId) {
      setMessage("Could not process this request because details are missing.");
      closeActionModal();
      return;
    }

    setBusyId(requestId);
    setMessage("");

    try {
      if (actionModal.action === "approve") {
        await updateStaffHolidayRequest({
          staffEmail: request.staffEmail,
          requestId,

          leaveType: request.leaveType,
          startDate: request.startDate,
          endDate: request.endDate,
          startDayType: request.startDayType || DAY_TYPES.FULL,
          endDayType: request.endDayType || DAY_TYPES.FULL,
          staffNotes: request.staffNotes || "",
          reason: request.reason || "",

          status: LEAVE_STATUS.APPROVED,
          updatedBy: currentUserEmail,
          updateReason:
            actionModal.reason.trim() || "Approved by staff holiday admin.",

          adminEdit: true,
          notifyStaff: true,
        });

        setMessage("Holiday request approved and staff member notified.");
        setApprovalSuccessBanner("Request approved successfully.");
      }

      if (actionModal.action === "reject") {
        await rejectStaffHolidayRequest({
          staffEmail: request.staffEmail,
          requestId,
          rejectedBy: currentUserEmail,
          rejectionReason: actionModal.reason.trim(),
        });

        setMessage("Holiday request rejected and staff member notified.");
      }

      if (actionModal.action === "delete") {
        await deleteStaffHolidayRequest({
          staffEmail: request.staffEmail,
          requestId,
          deletedBy: currentUserEmail,
          deleteReason:
            actionModal.reason.trim() || "Holiday request deleted by admin.",
          notifyStaff: true,
        });

        setMessage("Holiday request deleted and staff member notified.");
      }

      closeActionModal();
    } catch (error) {
      setMessage(error.message || "Failed to process holiday request.");
    } finally {
      setBusyId("");
    }
  };

  const openEmailPreview = (request) => {
    setPreview({
      subject:
        request.emailPreview?.adminSubject ||
        request.emailPreview?.holidayManagerSubject ||
        request.emailPreview?.managerSubject ||
        request.emailPreview?.staffSubject ||
        "Holiday Request",
      body:
        request.emailPreview?.adminBody ||
        request.emailPreview?.holidayManagerBody ||
        request.emailPreview?.managerBody ||
        request.emailPreview?.staffBody ||
        "",
    });
  };

  const renderLeaveStatCards = (cards = [], compact = false) => (
    <div
      className={`staff-holiday-approval-stats-grid ${
        compact ? "staff-holiday-approval-stats-grid--compact" : ""
      }`}
    >
      {cards.map((card) => (
        <div
          key={card.id}
          className={`staff-holiday-approval-stat-card staff-holiday-approval-stat-card--${card.tone}`}
        >
          <span>{card.label}</span>
          <strong>{card.value}</strong>
          <p>{card.description}</p>
        </div>
      ))}
    </div>
  );

  const formatAuditNote = (audit = {}) => {
    const note = String(audit.note || "").trim();
    const actor = audit.actionedBy || currentUserEmail || "Unknown user";

    if (!note) return "";

    if (/cancelled by staff member/i.test(note)) {
      return `Cancelled by ${actor}.`;
    }

    if (/holiday request cancelled/i.test(note)) {
      return `Holiday request cancelled by ${actor}.`;
    }

    if (/approved by staff holiday admin/i.test(note)) {
      return `Approved by ${actor}.`;
    }

    if (/holiday request approved by admin/i.test(note)) {
      return `Holiday request approved by ${actor}.`;
    }

    if (/holiday added and auto-approved by admin/i.test(note)) {
      return `Holiday added and approved by ${actor}.`;
    }

    if (/holiday request deleted by admin/i.test(note)) {
      return `Holiday request deleted by ${actor}.`;
    }

    return note;
  };
  const renderAuditTrail = (item = {}) => {
    const auditTrail = getAuditTrail(item);

    if (auditTrail.length === 0) {
      return null;
    }

    return (
      <details className="staff-holiday-audit-trail">
        <summary
          className="FP_Content_Semibold"
          style={{ marginBottom: 8, marginTop: 12, cursor: "pointer" }}
        >
          Action Trail ({auditTrail.length})
        </summary>

        <div className="staff-holiday-audit-list">
          {[...auditTrail].reverse().map((audit, index) => (
            <div
              key={audit.id || `${audit.actionedAt}-${index}`}
              className="staff-holiday-audit-item"
            >
              <div className="FP_Content_Semibold">
                {formatAuditAction(audit.action)}
              </div>

              <span className="FP_Help_Text grey">
                {formatAuditDateTime(audit.actionedAt)} by{" "}
                {audit.actionedBy || "Unknown"}
              </span>

              {formatAuditNote(audit) && (
                <p className="FP_Help_Text grey">{formatAuditNote(audit)}</p>
              )}

              {(audit.before || audit.after) && (
                <div className="staff-holiday-audit-change">
                  {renderAuditChanges(audit.before, audit.after)}
                </div>
              )}
            </div>
          ))}
        </div>
      </details>
    );
  };

  const renderRequestCard = (
    request,
    showActions = true,
    selectable = false,
    showSummaryCards = true,
    historyIndex = null,
  ) => {
    const requestId = getRequestId(request);
    const staffEmail = getRequestStaffEmail(request);
    const staffName = getRequestStaffName(request);
    const requestStaffProfile = summaryStaffProfileByEmail.get(
      normaliseEmail(staffEmail),
    );

    const canApprove =
      !isApprovedStatus(request.status) && !isCancelledStatus(request.status);
    const canReject = isPendingApprovalStatus(request.status);
    const canDelete = true;
    const isTrackerImport = Boolean(request.isTrackerImport);
    const isSelected =
      selectable && requestId === getRequestId(selectedPendingRequest);

    const selectRequest = () => {
      if (!selectable) return;
      setSelectedPendingRequestId(requestId);
    };

    return (
      <div
        key={requestId}
        className={`staff-holiday-request-card ${
          selectable ? "staff-holiday-request-card--selectable" : ""
        } ${isSelected ? "staff-holiday-request-card--selected" : ""}`}
        role={selectable ? "button" : undefined}
        tabIndex={selectable ? 0 : undefined}
        aria-pressed={selectable ? isSelected : undefined}
        onClick={selectRequest}
        onKeyDown={(event) => {
          if (!selectable || event.currentTarget !== event.target) return;

          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            selectRequest();
          }
        }}
      >
        <div
          className={`staff-holiday-request-card-content ${isTrackerImport ? "staff-holiday-request-card-content--tracker-import" : ""}`}
        >
          <div className="staff-holiday-request-title">
            <h3>
              {historyIndex !== null && `${historyIndex}. `}
              {staffName}
            </h3>

            <StatusButton
              type={LEAVE_STATUS_LABELS[request.status] || request.status}
              color={getStatusTone(request.status)}
            />
          </div>

          <p>
            {ADMIN_LEAVE_TYPE_LABELS[request.leaveType] || request.leaveType} ·{" "}
            {formatDisplayDate(request.startDate)} to{" "}
            {formatDisplayDate(request.endDate)}
          </p>

          <p>
            <strong>{formatNumber(request.totalDays)}</strong> day(s) ·{" "}
            {request.departmentName || "No department"}
          </p>

          {staffEmail && (
            <p className="FP_Help_Text grey">
              <strong>Email:</strong> {staffEmail}
            </p>
          )}

          {request.staffNotes && (
            <p className="FP_Help_Text grey">
              <strong>Notes:</strong> {request.staffNotes}
            </p>
          )}

          {request.isTrackerImport && (
            <p className="FP_Help_Text grey">
              <strong>Source:</strong> Imported tracker
              {request.importedFrom ? ` · ${request.importedFrom}` : ""}
            </p>
          )}

          {showSummaryCards && requestStaffProfile?.statCards?.length > 0 &&
            renderLeaveStatCards(requestStaffProfile.statCards, true)}

          {renderAuditTrail(request)}
        </div>

        <div className="staff-holiday-actions-column">
          {/* <button
            type="button"
            className="staff-holiday-secondary-button"
            disabled={busyId === requestId}
            onClick={() => openEmailPreview(request)}
          >
            View Email
          </button> */}

          <button
            type="button"
            className="staff-holiday-secondary-button"
            disabled={busyId === requestId}
            onClick={(event) => {
              event.stopPropagation();
              handleEditRequest(request);
            }}
          >
            Edit
          </button>

          {showActions && canApprove && (
            <button
              type="button"
              className="staff-holiday-primary-button"
              disabled={busyId === requestId}
              onClick={(event) => {
                event.stopPropagation();
                openActionModal(request, "approve");
              }}
            >
              Approve
            </button>
          )}

          {showActions && canReject && (
            <button
              type="button"
              className="staff-holiday-danger-button"
              disabled={busyId === requestId}
              onClick={(event) => {
                event.stopPropagation();
                openActionModal(request, "reject");
              }}
            >
              Reject
            </button>
          )}

          {/* {canDelete && (
            <button
              type="button"
              className="staff-holiday-danger-button"
              disabled={busyId === requestId}
              onClick={() => openActionModal(request, "delete")}
            >
              Delete
            </button>
          )} */}
        </div>
      </div>
    );
  };

  const renderAddHolidayTab = () => {
    if (staffDirectoryLoading) {
      return <p className="FP_Body grey">Loading staff profiles...</p>;
    }

    return (
      <div className="staff-holiday-form">
        {createForm.editRequestId && (
          <div className="staff-holiday-message">
            Editing existing holiday request. Saving will notify the staff
            member by email.
          </div>
        )}

        <div className="staff-holiday-form-row">
          <div className="staff-holiday-form-field">
            <label className="ds-input-label">Staff member</label>
            <CustomSelect
              options={staffSelectOptions}
              value={createForm.staffEmail}
              onChange={(event) =>
                handleCreateFieldChange("staffEmail", event.target.value)
              }
              placeholder="Select staff member"
            />
          </div>

          <div className="staff-holiday-form-field">
            <label className="ds-input-label">Leave type</label>
            <CustomSelect
              options={leaveTypeOptions}
              value={createForm.leaveType}
              onChange={(event) =>
                handleCreateFieldChange("leaveType", event.target.value)
              }
              searchable={false}
              clearable={false}
            />
          </div>
        </div>

        <div className="staff-holiday-form-row">
          <DateField
            label="Start date"
            value={createForm.startDate}
            onChange={(event) =>
              handleCreateFieldChange("startDate", event.target.value)
            }
          />

          <DateField
            label="End date"
            value={createForm.endDate}
            onChange={(event) =>
              handleCreateFieldChange("endDate", event.target.value)
            }
          />
        </div>

        <div className="staff-holiday-form-row">
          <div className="staff-holiday-form-field">
            <label className="ds-input-label">Start day type</label>
            <CustomSelect
              options={dayTypeOptions}
              value={createForm.startDayType}
              onChange={(event) =>
                handleCreateFieldChange("startDayType", event.target.value)
              }
              searchable={false}
              clearable={false}
            />
          </div>

          <div className="staff-holiday-form-field">
            <label className="ds-input-label">End day type</label>
            <CustomSelect
              options={dayTypeOptions}
              value={createForm.endDayType}
              onChange={(event) =>
                handleCreateFieldChange("endDayType", event.target.value)
              }
              searchable={false}
              clearable={false}
            />
          </div>
        </div>

        {createForm.editRequestId && (
          <div className="staff-holiday-form-row">
            <div className="staff-holiday-form-field">
              <label className="ds-input-label">Request status</label>
              <CustomSelect
                options={statusOptions}
                value={createForm.status}
                onChange={(event) =>
                  handleCreateFieldChange("status", event.target.value)
                }
                searchable={false}
                clearable={false}
              />
            </div>

            {/* <div className="staff-holiday-form-field">
              <label className="ds-input-label">Update note</label>
              <input
                type="text"
                value={createForm.updateReason}
                onChange={(event) =>
                  handleCreateFieldChange("updateReason", event.target.value)
                }
                placeholder="Optional note shown in staff email"
              />
            </div> */}
          </div>
        )}

        <TextArea
          label="Notes"
          value={createForm.staffNotes}
          onChange={(event) =>
            handleCreateFieldChange("staffNotes", event.target.value)
          }
          placeholder="Optional note for this request"
          rows={4}
        />

        <div className="staff-holiday-actions">
          <button
            type="button"
            className="staff-holiday-primary-button"
            disabled={createBusy}
            onClick={handleCreateRequest}
          >
            {createBusy
              ? createForm.editRequestId
                ? "Saving..."
                : "Creating..."
              : createForm.editRequestId
                ? "Save Changes & Notify Staff"
                : "Add & Approve Holiday"}
          </button>

          {createForm.editRequestId && (
            <button
              type="button"
              className="staff-holiday-secondary-button"
              disabled={createBusy}
              onClick={resetCreateForm}
            >
              Cancel Edit
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderPendingRequestTab = () => {
    const yearSelector = (
      <div className="staff-holiday-year-selector">
        <RadioGroup
          label="Holiday year"
          name="holiday-pending-year"
          options={[
            {
              value: CURRENT_HOLIDAY_YEAR,
              label: String(CURRENT_HOLIDAY_YEAR),
            },
            {
              value: CURRENT_HOLIDAY_YEAR + 1,
              label: String(CURRENT_HOLIDAY_YEAR + 1),
            },
          ]}
          value={summaryYear}
          onChange={setSummaryYear}
          columns={2}
        />
      </div>
    );

    if (requestsLoading || summaryStaffDirectoryLoading) {
      return (
        <>
          {yearSelector}
          <p className="FP_Body grey">Loading requests...</p>
        </>
      );
    }

    if (pendingRequests.length === 0) {
      return (
        <>
          {yearSelector}
          <p className="FP_Body grey">
            No pending requests found for {summaryYear}.
          </p>
        </>
      );
    }

    return (
      <>
        {yearSelector}
        <div className="staff-holiday-pending-split">
          <section className="staff-holiday-pending-list-pane">
            <div className="staff-holiday-request-list">
              {pendingRequests.map((request) =>
                renderRequestCard(request, true, true),
              )}
            </div>
          </section>

          <section className="staff-holiday-pending-tracker-pane">
            {selectedPendingRequest && (
              <>
              <div className="staff-holiday-selected-request-header">
                <div>
                  <h3 className="FP_Content_Semibold">
                    {getRequestStaffName(selectedPendingRequest)}
                  </h3>
                  <p className="FP_Help_Text grey">
                    {formatDisplayDate(selectedPendingRequest.startDate)} to{" "}
                    {formatDisplayDate(selectedPendingRequest.endDate)} ·{" "}
                    {selectedRequestDepartment.name}
                  </p>
                </div>

                <StatusButton
                  type={
                    LEAVE_STATUS_LABELS[selectedPendingRequest.status] ||
                    selectedPendingRequest.status
                  }
                  color={getStatusTone(selectedPendingRequest.status)}
                />
              </div>

              <StaffHolidayTrackerPanel
                key={getRequestId(selectedPendingRequest)}
                focusDate={selectedPendingRequest.startDate || ""}
                initialYear={summaryYear}
                initialMonthIndex={new Date().getMonth()}
                departmentId={selectedRequestDepartment.id || "all"}
                departmentName={selectedRequestDepartment.name || ""}
                highlightStartDate={selectedPendingRequest.startDate || ""}
                highlightEndDate={selectedPendingRequest.endDate || ""}
                highlightStaffEmail={getRequestStaffEmail(
                  selectedPendingRequest,
                )}
                highlightLeaveType={
                  selectedPendingRequest.leaveType || LEAVE_TYPES.ANNUAL_LEAVE
                }
                highlightStartDayType={
                  selectedPendingRequest.startDayType || DAY_TYPES.FULL
                }
                highlightEndDayType={
                  selectedPendingRequest.endDayType || DAY_TYPES.FULL
                }
                compact
              />
              </>
            )}
          </section>
        </div>
      </>
    );
  };

  const renderSummaryTab = () => {
    const yearSelector = (
      <div className="staff-holiday-year-selector">
        <RadioGroup
          label="Holiday year"
          name="holiday-summary-year"
          options={[
            {
              value: CURRENT_HOLIDAY_YEAR,
              label: String(CURRENT_HOLIDAY_YEAR),
            },
            {
              value: CURRENT_HOLIDAY_YEAR + 1,
              label: String(CURRENT_HOLIDAY_YEAR + 1),
            },
          ]}
          value={summaryYear}
          onChange={setSummaryYear}
          columns={2}
        />
      </div>
    );

    if (requestsLoading || summaryStaffDirectoryLoading) {
      return (
        <>
          {yearSelector}
          <p className="FP_Body grey">Loading summary...</p>
        </>
      );
    }

    if (summaryStaffProfiles.length === 0) {
      return (
        <>
          {yearSelector}
          <p className="FP_Body grey">
            No staff profiles found for {summaryYear}.
          </p>
        </>
      );
    }

    return (
      <>
        {yearSelector}

        {renderLeaveStatCards(overallStatCards)}

        <div className="staff-holiday-collapsible-list">
          {summaryStaffProfiles.map((staff) => {
            const latestStaffEntries = getLatestLeaveHistoryEntries([
              ...staff.requests,
              ...(staff.trackerHistory || staff.trackerEntries || []),
            ]);
            const staffPendingRequests = latestStaffEntries.filter((request) =>
              isPendingApprovalStatus(request.status),
            );
            const staffHistory = latestStaffEntries.filter(
                (request) => !isPendingApprovalStatus(request.status),
              ).sort((a, b) => {
              const dateA = String(a.startDate || a.date || "");
              const dateB = String(b.startDate || b.date || "");

              return dateB.localeCompare(dateA);
            });
            const totalEntries = staffHistory.length;
            const unpaidDays = staffHistory.filter(
              (item) =>
                item.leaveType === LEAVE_TYPES.UNPAID_LEAVE &&
                isApprovedStatus(item.status),
            ).reduce(
              (total, item) => total + Number(item.totalDays ?? item.dayValue ?? 0),
              0,
            );

            const totalDaysRecorded = staffHistory.reduce(
              (total, item) =>
                total + Number(item.totalDays ?? item.dayValue ?? 0),
              0,
            );

            const approvedEntries = staffHistory.filter(
              (item) =>
                String(item.status || "").toLowerCase() ===
                LEAVE_STATUS.APPROVED,
            ).length;
            return (
              <details
                key={staff.email}
                className={`staff-holiday-collapsible ${
                  isInactiveStaff(staff)
                    ? "staff-holiday-collapsible--inactive"
                    : ""
                }`}
              >
                <summary className="staff-holiday-collapsible-summary">
                  <div className="FP_Content_Semibold">
                    {staff.name}
                    <span>{staff.email}</span>
                  </div>

                  {staff.actionableCount > 0 && (
                    <div className="staff-holiday-collapsible-badges">
                      <StatusButton
                        type={`Pending ${staff.actionableCount}`}
                        color="yellow"
                      />
                    </div>
                  )}
                  <div className="staff-holiday-collapsible-arrow">
                    <ArrowDown />
                  </div>
                </summary>

                <div className="staff-holiday-collapsible-content">
                  {renderLeaveStatCards(staff.statCards)}

                  {/* <h3 className="FP_Subtitle">
                    Taken calculation breakdown
                  </h3>
                  {staff.leaveStats?.takenBreakdown?.length ? (
                    <div style={{ overflowX: "auto" }}>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          textAlign: "left",
                        }}
                      >
                        <thead>
                          <tr>
                            <th style={{ padding: "8px", border: "1px solid #ccc" }}>
                              Date
                            </th>
                            <th style={{ padding: "8px", border: "1px solid #ccc" }}>
                              Counted in Taken
                            </th>
                            <th style={{ padding: "8px", border: "1px solid #ccc" }}>
                              Firebase LeaveRequest document ID(s)
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {staff.leaveStats.takenBreakdown.map((item) => (
                            <tr key={item.date}>
                              <td
                                style={{
                                  padding: "8px",
                                  border: "1px solid #ccc",
                                }}
                              >
                                {formatDisplayDate(item.date)}
                              </td>
                              <td
                                style={{
                                  padding: "8px",
                                  border: "1px solid #ccc",
                                }}
                              >
                                {formatNumber(item.days)}
                              </td>
                              <td
                                style={{
                                  padding: "8px",
                                  border: "1px solid #ccc",
                                }}
                              >
                                {item.firebaseIds.map((firebaseId) => (
                                  <div key={firebaseId}>
                                    <code>{firebaseId}</code>
                                  </div>
                                ))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <th style={{ padding: "8px", border: "1px solid #ccc" }}>
                              Total
                            </th>
                            <th style={{ padding: "8px", border: "1px solid #ccc" }}>
                              {formatNumber(staff.leaveStats.taken)}
                            </th>
                            <th style={{ padding: "8px", border: "1px solid #ccc" }}>
                              —
                            </th>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ) : (
                    <p className="FP_Help_Text grey">
                      No approved annual leave dates are counted in Taken.
                    </p>
                  )} */}

                  <div className="staff-holiday-profile-grid">
                    <div className="FP_Content_Semibold">
                      <span>Department</span>
                      {staff.departmentName || "No department"}
                    </div>

                    <div className="FP_Content_Semibold">
                      <span>Total Entries</span>
                      {formatNumber(totalEntries)}
                    </div>

                    <div className="FP_Content_Semibold">
                      <span>Total Days Recorded</span>
                      {formatNumber(totalDaysRecorded)}
                    </div>

                    <div className="FP_Content_Semibold">
                      <span>Bradford Factor</span>
                      {formatNumber(staff.bradfordFactor || 0)}
                    </div>

                    <div className="FP_Content_Semibold">
                      <span>Sickness Instances</span>
                      {formatNumber(staff.sicknessInstances || 0)}
                    </div>

                    <div className="FP_Content_Semibold">
                      <span>Sickness Days</span>
                      {formatNumber(staff.sicknessDays || 0)}
                    </div>

                    <div className="FP_Content_Semibold">
                      <span>Approved</span>
                      {staff.approvedCount}
                    </div>

                    <div className="FP_Content_Semibold">
                      <span>Rejected</span>
                      {staff.rejectedCount}
                    </div>

                    <div className="FP_Content_Semibold">
                      <span>Cancelled</span>
                      {staff.cancelledCount}
                    </div>

                    <div className="FP_Content_Semibold">
                      <span>Employment</span>
                      {formatDisplayDate(staff.employmentStartDate) ||
                        "Unknown"}{" "}
                      to{" "}
                      {staff.employmentEndDate
                        ? formatDisplayDate(staff.employmentEndDate)
                        : "Present"}
                    </div>

                    <div className="FP_Content_Semibold">
                      <span>Unpaid Days</span>
                      {formatNumber(unpaidDays)}
                    </div>
                  </div>

                  <h3 className="FP_Subtitle">Pending Requests</h3>
                  {staffPendingRequests.length === 0 ? (
                    <p className="FP_Help_Text grey">
                      No pending requests for this staff member.
                    </p>
                  ) : (
                    <div className="staff-holiday-request-list">
                      {staffPendingRequests.map((request) =>
                        renderRequestCard(request, true),
                      )}
                    </div>
                  )}

                  <h3 className="FP_Subtitle">Past History</h3>
                  {staffHistory.length === 0 ? (
                    <p className="FP_Help_Text grey">
                      No past history for this staff member.
                    </p>
                  ) : (
                    <div className="staff-holiday-request-list">
                      {staffHistory.map((request, index) =>
                        renderRequestCard(request, true, false, false, index + 1),
                      )}
                    </div>
                  )}
                </div>
              </details>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <div className="Main-Page">
      <Helmet>
        <title>Holiday Approvals</title>
      </Helmet>
      <Sidebar />

      <div className="Main-Container">
        <main className="staff-holiday-page">
          <Breadcrumb
            items={[
              { label: "All Staff", to: "/Department/AllStaff" },
              { label: "My Holiday", to: "/staff-holiday" },
              { label: "Holiday Approvals" },
            ]}
          />

          <div className="staff-holiday-header">
            <div>
              {/* <p className="staff-holiday-eyebrow">Staff Holiday</p> */}
              <div className="FP_H3 staff-holiday-title">Holiday Approvals</div>
              {/* <p className="FP_Help_Text grey">
                Review, approve, edit and delete holiday requests.
              </p> */}
            </div>
            {canDownloadExcel && (
              <StaffHolidayExcelDownload
                staffProfiles={filteredStaffProfiles}
                requests={requests}
                summaryYear={CURRENT_HOLIDAY_YEAR}
                disabled={requestsLoading || staffDirectoryLoading}
              />
            )}
          </div>

          {isSuperAdmin && holidayManagerViewOptions.length > 1 && (
            <RadioGroup
              label="Holiday manager view"
              name="holiday-manager-view"
              options={holidayManagerViewOptions}
              value={selectedHolidayManagerView}
              onChange={setSelectedHolidayManagerView}
              columns={3}
            />
          )}

          {approvalSuccessBanner && (
            <Banner type="success" message={approvalSuccessBanner} />
          )}

          {/* {message && <div className="staff-holiday-message">{message}</div>} */}

          {/* {requestsError && (
            <div className="staff-holiday-message">
              {requestsError.message || "Could not load holiday requests."}
            </div>
          )} */}

          <div className="staff-holiday-card">
            <Tabs
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>

          <div
            className={`staff-holiday-card staff-holiday-tab-card--${activeTab}`}
          >
            {activeTab === TAB_IDS.PENDING_REQUEST && renderPendingRequestTab()}
            {activeTab === TAB_IDS.ADD_HOLIDAY && renderAddHolidayTab()}
            {activeTab === TAB_IDS.SUMMARY && renderSummaryTab()}
          </div>

          <StaffHolidayEmailPreviewModal
            open={Boolean(preview)}
            subject={preview?.subject}
            body={preview?.body}
            onClose={() => setPreview(null)}
          />

          <StaffHolidayActionConfirmModal
            open={actionModal.open}
            request={actionModal.request}
            title={
              actionModal.action === "delete"
                ? "Delete holiday request?"
                : actionModal.action === "reject"
                  ? "Reject holiday request?"
                  : "Approve holiday request?"
            }
            description={
              actionModal.action === "delete"
                ? "This will delete the holiday request. If it was approved, the balance and tracker entries will be reversed."
                : actionModal.action === "reject"
                  ? "This will reject the holiday request and notify the staff member."
                  : "This will approve the request, update the balance, add tracker entries and notify the staff member."
            }
            confirmLabel={
              actionModal.action === "delete"
                ? "Delete Request"
                : actionModal.action === "reject"
                  ? "Reject Request"
                  : "Approve Request"
            }
            tone={
              actionModal.action === "delete" || actionModal.action === "reject"
                ? "danger"
                : "primary"
            }
            busy={Boolean(busyId)}
            showReason
            reason={actionModal.reason}
            reasonLabel={
              actionModal.action === "delete"
                ? "Delete reason"
                : actionModal.action === "reject"
                  ? "Rejection reason"
                  : "Approval note"
            }
            reasonPlaceholder="Optional note"
            onReasonChange={updateActionReason}
            onConfirm={confirmActionModal}
            onClose={closeActionModal}
          />
        </main>
      </div>
    </div>
  );
};

export default StaffHolidayApprovalsPage;
